import React, { useEffect, useMemo, useState } from 'react';
import ChevronDown from '@uber/base-icons/chevron-down';
import DiamondEmpty from '@uber/base-icons/diamond-empty';
import { useStyletron } from 'baseui';
import { Button, KIND as ButtonKind, SIZE as ButtonSize } from 'baseui/button';
import { PLACEMENT, StatefulPopover } from 'baseui/popover';
import { Tag } from 'baseui/tag';
import { ParagraphSmall } from 'baseui/typography';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import type { StyleObject } from 'styletron-react';
import { ACTION_BY } from '../../../../../common/constant/constant';
import useAnalyticsForSidePanel from '../../../../../common/side-panel/analytics/useAnalyticsForSidePanel';
import type { AnnotationLabelT } from '../../../../../common/types/types';
import { colorWithOpacity } from '../../../../../common/utils/color-with-opacity';
import { type ModernizedGenericAnnotation, type SidePanelDispatchAtom, LabelModificationSelector } from '../../../../../modernization-common/side-panel';
import { ActionModal } from '../../../../../modernization-common/side-panel/common';
import { SIDE_PANEL_ACTIONS } from '../../../../../modernization-common/side-panel/const';
import { getToolIcon } from '../../../../labelling/components/toolbar/utils';
import type { AnnotationT } from '../../../../labelling/types';
import { annotationLabelsAtom } from '../../../atoms/annotation-atoms';
import { useEditAnnotationForm } from '../../side-panel/hooks/use-edit-annotation-form';
import type { SelectedAnnotationDetailsT } from '../../side-panel/types';
import { getAnnotationLabelIcon } from '../utils/get-annotation-label-icon';
import { useSelectedAnnotationDetails } from './utils';
const SWITCH_LABEL_ADDITIONAL_INFO = 'Switching to this label would clear the current answers';
const LABEL_CHANGE_CONFIRMATION_COPY = 'This will reset all metadata, dynamic attributes and delete any relationships for this object. Only tracking data will be preserved.';
type ActionModalStyleOverridesT = {
  dialog?: StyleObject;
};
const getLabelChangeModalStyleOverrides = (): ActionModalStyleOverridesT => ({
  dialog: {
    width: '390px',
    margin: 0
  }
});
const applyLabelChange = ({
  value,
  selectedAnnotationDetails,
  annotationLabels,
  handleLabelChange,
  dispatch,
  sendEventForUpdateAnnotationLabel
}: {
  value: string;
  selectedAnnotationDetails: SelectedAnnotationDetailsT;
  annotationLabels: AnnotationLabelT[];
  handleLabelChange: (value: string) => void;
  dispatch: (action: unknown) => void;
  sendEventForUpdateAnnotationLabel: ReturnType<typeof useAnalyticsForSidePanel>['sendEventForUpdateAnnotationLabel'];
}) => {
  const newLabel = annotationLabels.find(label => label.title === value);
  if (!newLabel) {
    return;
  }
  handleLabelChange(value);
  dispatch({
    type: SIDE_PANEL_ACTIONS.UPDATE_ANNOTATION_LABEL,
    annotationIdx: selectedAnnotationDetails.id,
    annotationLabel: newLabel
  });
  if (selectedAnnotationDetails.annotationLabel.title !== value) {
    sendEventForUpdateAnnotationLabel({
      objectType: selectedAnnotationDetails.annotationType,
      prevLabel: selectedAnnotationDetails.annotationLabel.title,
      newLabel: newLabel.title,
      objectId: selectedAnnotationDetails.id,
      actionBy: ACTION_BY.EDIT
    });
  }
};
export const LabelModification = ({
  selectedAnnotationDetails,
  annotationLabels,
  dispatchAtom,
  header = 'Class',
  wrapperStyles
}: {
  selectedAnnotationDetails: SelectedAnnotationDetailsT;
  annotationLabels: AnnotationLabelT[];
  dispatchAtom: SidePanelDispatchAtom<AnnotationT & ModernizedGenericAnnotation>;
  header?: string;
  wrapperStyles?: StyleObject;
}) => {
  const [, theme] = useStyletron();
  const dispatch = useSetAtom(dispatchAtom);
  const {
    sendEventForUpdateAnnotationLabel
  } = useAnalyticsForSidePanel();
  const {
    selectedLabel,
    hasQuestionnaire,
    compatibleLabels,
    handleLabelChange
  } = useEditAnnotationForm({
    selectedAnnotationDetails,
    annotationLabels
  });
  const options = useMemo(() => {
    return compatibleLabels.map(label => {
      const isNotCurrentLabel = selectedLabel !== label.title;
      return {
        label: label.title,
        value: label.title,
        additionalInfo: hasQuestionnaire && isNotCurrentLabel ? SWITCH_LABEL_ADDITIONAL_INFO : undefined,
        startEnhancer: (() => {
          const labelTool = label.tools?.[0];
          if (labelTool) {
            const Icon = getToolIcon((labelTool as Parameters<typeof getToolIcon>[0]));
            return <Icon strokeColor={label.style.strokeColor} size={14} />;
          }
          return <DiamondEmpty color={label.style.strokeColor} size={14} />;
        })()
      };
    });
  }, [compatibleLabels, selectedLabel, hasQuestionnaire]);
  return <LabelModificationSelector options={options} currentValue={selectedLabel || ''} onChange={value => {
    if (!value) {
      return;
    }
    applyLabelChange({
      value,
      selectedAnnotationDetails,
      annotationLabels,
      handleLabelChange,
      dispatch,
      sendEventForUpdateAnnotationLabel
    });
  }} header={header} wrapperTitleColor={theme.colors.contentTertiary} wrapperStyles={wrapperStyles ?? {
    paddingTop: theme.sizing.scale0
  }} />;
};
const activeLabelAtom = atom<AnnotationLabelT | null>(null);
export const HeaderAnnotationIcon = () => {
  const activeLabel = useAtomValue(activeLabelAtom);
  if (!activeLabel) return null;
  return getAnnotationLabelIcon(activeLabel, 16);
};
export const HeaderLabelModification = ({
  annotationId,
  dispatchAtom
}: {
  annotationId: string;
  dispatchAtom: SidePanelDispatchAtom<AnnotationT & ModernizedGenericAnnotation>;
}) => {
  const [css, theme] = useStyletron();
  const [pendingLabelTitle, setPendingLabelTitle] = useState<string | null>(null);
  const dispatch = useSetAtom(dispatchAtom);
  const {
    sendEventForUpdateAnnotationLabel
  } = useAnalyticsForSidePanel();
  const annotationLabels = useAtomValue(annotationLabelsAtom);
  const selectedAnnotationDetails = useSelectedAnnotationDetails(annotationId);
  const {
    selectedLabel,
    compatibleLabels,
    handleLabelChange
  } = useEditAnnotationForm({
    selectedAnnotationDetails,
    annotationLabels
  });
  const activeLabel = useMemo(() => annotationLabels.find(label => label.title === selectedLabel) || selectedAnnotationDetails?.annotationLabel, [annotationLabels, selectedLabel, selectedAnnotationDetails?.annotationLabel]);
  const setActiveLabel = useSetAtom(activeLabelAtom);
  useEffect(() => {
    setActiveLabel(activeLabel ?? null);
  }, [activeLabel, setActiveLabel]);
  if (!selectedAnnotationDetails || !activeLabel) {
    return null;
  }
  const activeLabelColor = activeLabel.style.strokeColor || theme.colors.contentAccent;
  const activeTagColor = colorWithOpacity(activeLabelColor, 0.2);
  const activeLabelTitle = selectedLabel || selectedAnnotationDetails.annotationLabel.title;
  const activeAnnotationName = `${activeLabelTitle} [${selectedAnnotationDetails.id}]`;
  const pendingLabel = pendingLabelTitle ? annotationLabels.find(label => label.title === pendingLabelTitle) : undefined;
  const options = compatibleLabels.filter(label => label.title !== activeLabelTitle).map(label => {
    const labelTool = label.tools?.[0];
    const OptionIcon = labelTool ? getToolIcon((labelTool as Parameters<typeof getToolIcon>[0])) : null;
    const optionColor = label.style.strokeColor;
    return {
      label,
      startEnhancer: OptionIcon ? <OptionIcon strokeColor={optionColor} size={14} /> : <DiamondEmpty color={optionColor} size={14} />
    };
  });
  const handleConfirmLabelChange = () => {
    if (!pendingLabel) {
      return;
    }
    applyLabelChange({
      value: pendingLabel.title,
      selectedAnnotationDetails,
      annotationLabels,
      handleLabelChange,
      dispatch,
      sendEventForUpdateAnnotationLabel
    });
    setPendingLabelTitle(null);
  };
  const hasNoAlternativeLabels = options.length === 0;
  const activeLabelTag = <Tag kind="custom" closeable={false} overrides={{
    Root: {
      props: {
        style: {
          backgroundColor: activeTagColor
        }
      },
      style: {
        margin: 0,
        border: 'none',
        backgroundColor: activeTagColor,
        width: 'max-content',
        opacity: 1
      }
    },
    Text: {
      style: {
        ...theme.typography.LabelXSmall,
        color: activeLabelColor
      }
    }
  }}>
      <span style={{
      color: activeLabelColor
    }} className={css({
      display: 'flex',
      alignItems: 'center',
      gap: theme.sizing.scale100,
      color: activeLabelColor
    })}>
        {activeLabelTitle}
        {!hasNoAlternativeLabels && <ChevronDown size={14} />}
      </span>
    </Tag>;
  return <>
      {hasNoAlternativeLabels ? activeLabelTag : <StatefulPopover content={({
      close
    }) => <div className={css({
      backgroundColor: theme.colors.backgroundSecondary,
      borderRadius: theme.borders.radius300,
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      gap: theme.sizing.scale0,
      height: 'auto',
      opacity: 1,
      overflowY: 'auto',
      padding: theme.sizing.scale300,
      minWidth: '128px',
      width: 'auto'
    })}>
              {options.map(({
        label,
        startEnhancer
      }) => <Button key={label.title} data-testid={`header-label-option-${label.title}`} kind={ButtonKind.tertiary} size={ButtonSize.compact} startEnhancer={() => startEnhancer} onClick={() => {
        setPendingLabelTitle(label.title);
        close();
      }} overrides={{
        BaseButton: {
          style: {
            justifyContent: 'flex-start',
            minHeight: theme.sizing.scale650,
            padding: theme.sizing.scale300,
            width: '100%'
          }
        }
      }}>
                  <span className={css({
          ...theme.typography.LabelSmall,
          color: theme.colors.contentPrimary
        })}>
                    {label.title}
                  </span>
                </Button>)}
            </div>} placement={PLACEMENT.bottomLeft} returnFocus>
          <Button data-testid="header-label-selector" kind={ButtonKind.tertiary} size={ButtonSize.compact} overrides={{
        BaseButton: {
          style: {
            padding: 0,
            margin: 0,
            minHeight: 'auto'
          }
        }
      }}>
            {activeLabelTag}
          </Button>
        </StatefulPopover>}
      <ActionModal isOpen={!!pendingLabel} onClose={() => setPendingLabelTitle(null)} customStyleOverrides={getLabelChangeModalStyleOverrides()} title={<span className={css({
      ...theme.typography.HeadingXSmall
    })}>
            Change label of {activeAnnotationName} to {pendingLabel?.title} ?
          </span>} actions={[{
      label: 'Cancel',
      onClick: () => setPendingLabelTitle(null)
    }, {
      label: 'Change label',
      onClick: handleConfirmLabelChange
    }]}>
        <ParagraphSmall margin={0}>{LABEL_CHANGE_CONFIRMATION_COPY}</ParagraphSmall>
      </ActionModal>
    </>;
};